import { useContext, useMemo, useState } from 'react'
import BlockContent from '@sanity/block-content-to-react'

import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { serializers } from '@lib/serializers'
import { StringsContext } from '@lib/strings'

import ProductActions from '@blocks/product/product-actions'
import ProductForm from '@blocks/product/product-form'
import ProductGallery from '@blocks/product/product-gallery'
import ProductPrice from '@blocks/product/product-price'
import Label from '@components/label'

interface ProductHeroProps {
  product: SanityProductFragment
  activeVariant: SanityProductVariantFragment
  onVariantChange: (variantId: number) => void
}

const ProductHero = ({
  product,
  activeVariant,
  onVariantChange,
}: ProductHeroProps) => {
  const strings = useContext(StringsContext)

  const [previewVariantId, setPreviewVariantId] = useState<number | null>(null)
  const previewVariant = useMemo(
    () => product.variants?.find(({ id }) => id === previewVariantId),
    [previewVariantId, product.variants]
  )

  const isLowStock = activeVariant.lowStock && activeVariant.inStock
  const isOutOfStock = !activeVariant.inStock

  return (
    <section className="container xs:py-8 md:py-24">
      <div className="relative grid grid-cols-1 md:grid-cols-12">
        <div className="relative mb-12 md:mb-0 md:col-span-6 bg-pageBG">
          <ProductGallery
            photosets={product.photos.main ?? []}
            activeVariant={activeVariant}
            previewVariant={previewVariant}
            hasArrows
            hasCounter
            hasDrag
            preloadFirstPhoto
          />
        </div>

        <div className="container bg-pageBG flex flex-col md:pl-14 lg:pl-20 md:col-span-6">
          <div className="max-w-2xl mx-auto">
            <div className="mb-8 md:mb-10">
              <div className="flex justify-between mb-4">
                <div className="mb-4">
                  {activeVariant && (
                    <div className="flex items-center mb-2 text-sm leading-relaxed tracking-wider font-semibold uppercase">
                      {activeVariant.title}

                      {isLowStock && (
                        <Label
                          secondary
                          isBadge
                          hasDot
                          pulsingDot
                          className="mx-2"
                        >
                          {strings.productLowStock}
                        </Label>
                      )}

                      {isOutOfStock && (
                        <Label primary isBadge hasDot className="mx-2">
                          {strings.productOutOfStock}
                        </Label>
                      )}
                    </div>
                  )}

                  <h1 className="m-0 text-3xl leading-snug">{product.title}</h1>
                </div>
              </div>

              <ProductForm
                product={product}
                activeVariant={activeVariant}
                onVariantChange={onVariantChange}
                onPreviewVariant={setPreviewVariantId}
              />

              <div className="flex mt-2 py-2 items-center justify-between">
                <div className="mr-12 text-xs font-semibold uppercase tracking-wider">
                  {strings.productPriceLabel}
                </div>
                <ProductPrice
                  inProductHero
                  price={activeVariant.price ?? product.price}
                  comparePrice={
                    activeVariant.comparePrice ?? product.comparePrice
                  }
                />
              </div>
            </div>

            <div className="mb-8 md:mb-10">
              <ProductActions activeVariant={activeVariant} />
            </div>

            {product.description && (
              <div className="w-full mb-6">
                <BlockContent
                  renderContainerOnSingleChild
                  className="rc"
                  blocks={product.description}
                  serializers={serializers}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProductHero
